import {
    SHOP_DOCUMENT_ROLE_OPTION,
    SHOP_GRID_SLOT_KEY,
} from "../shopBlocks";
import type { ShopTemplateBlock } from "../types";

export const LAGUNA_FOOTER_LAYOUT_STYLES = [
    "editorial",
    "columns",
    "centered",
    "compact",
    "split",
    "stacked",
    "showcase",
    "minimal",
] as const;

export const LAGUNA_FOOTER_SKELETON_VERSION = "1";

export type LagunaFooterLayoutStyle =
    typeof LAGUNA_FOOTER_LAYOUT_STYLES[number];

export type LagunaFooterGridPlacement = Readonly<{
    h: number;
    w: number;
    x: number;
    y: number;
}>;

const LAGUNA_FOOTER_ROLES = [
    "footerService",
    "footerLinks",
    "footerDivider",
    "footerCopyright",
] as const;

type LagunaFooterRole = typeof LAGUNA_FOOTER_ROLES[number];

export const LAGUNA_FOOTER_LAYOUT_PLACEMENTS: Readonly<
    Record<
        LagunaFooterLayoutStyle,
        Readonly<Record<LagunaFooterRole, LagunaFooterGridPlacement>>
    >
> = {
    editorial: {
        footerService: { x: 1, y: 1, w: 4, h: 3 },
        footerLinks: { x: 6, y: 1, w: 7, h: 3 },
        footerDivider: { x: 1, y: 4, w: 12, h: 1 },
        footerCopyright: { x: 1, y: 5, w: 12, h: 1 },
    },
    columns: {
        footerService: { x: 1, y: 1, w: 3, h: 3 },
        footerLinks: { x: 4, y: 1, w: 9, h: 3 },
        footerDivider: { x: 1, y: 4, w: 12, h: 1 },
        footerCopyright: { x: 1, y: 5, w: 12, h: 1 },
    },
    centered: {
        footerService: { x: 3, y: 1, w: 8, h: 2 },
        footerLinks: { x: 2, y: 3, w: 10, h: 2 },
        footerDivider: { x: 2, y: 5, w: 10, h: 1 },
        footerCopyright: { x: 3, y: 6, w: 8, h: 1 },
    },
    compact: {
        footerService: { x: 1, y: 1, w: 4, h: 2 },
        footerLinks: { x: 5, y: 1, w: 8, h: 2 },
        footerDivider: { x: 1, y: 3, w: 12, h: 1 },
        footerCopyright: { x: 1, y: 4, w: 12, h: 1 },
    },
    split: {
        footerService: { x: 1, y: 1, w: 6, h: 4 },
        footerLinks: { x: 7, y: 1, w: 6, h: 4 },
        footerDivider: { x: 1, y: 5, w: 12, h: 1 },
        footerCopyright: { x: 1, y: 6, w: 12, h: 1 },
    },
    stacked: {
        footerService: { x: 1, y: 1, w: 8, h: 2 },
        footerLinks: { x: 1, y: 4, w: 12, h: 2 },
        footerDivider: { x: 1, y: 3, w: 12, h: 1 },
        footerCopyright: { x: 1, y: 6, w: 12, h: 1 },
    },
    showcase: {
        footerService: { x: 1, y: 1, w: 12, h: 2 },
        footerLinks: { x: 1, y: 3, w: 12, h: 3 },
        footerDivider: { x: 1, y: 6, w: 12, h: 1 },
        footerCopyright: { x: 2, y: 7, w: 10, h: 1 },
    },
    minimal: {
        footerService: { x: 1, y: 1, w: 5, h: 2 },
        footerLinks: { x: 6, y: 1, w: 7, h: 2 },
        footerDivider: { x: 1, y: 3, w: 12, h: 1 },
        footerCopyright: { x: 1, y: 4, w: 12, h: 1 },
    },
};

const rootLayoutOptions: Readonly<
    Record<LagunaFooterLayoutStyle, Readonly<Record<string, string>>>
> = {
    editorial: {
        minHeight: "486px",
        paddingBottom: "28px",
        paddingTop: "48px",
    },
    columns: {
        minHeight: "350px",
        paddingBottom: "28px",
        paddingTop: "44px",
    },
    centered: {
        minHeight: "430px",
        paddingBottom: "34px",
        paddingTop: "52px",
    },
    compact: {
        minHeight: "300px",
        paddingBottom: "24px",
        paddingTop: "32px",
    },
    split: {
        minHeight: "390px",
        paddingBottom: "28px",
        paddingTop: "48px",
    },
    stacked: {
        minHeight: "430px",
        paddingBottom: "30px",
        paddingTop: "50px",
    },
    showcase: {
        minHeight: "460px",
        paddingBottom: "34px",
        paddingTop: "54px",
    },
    minimal: {
        minHeight: "260px",
        paddingBottom: "22px",
        paddingTop: "28px",
    },
};

function roleLayoutOptions(
    layout: LagunaFooterLayoutStyle,
    role: LagunaFooterRole,
): Record<string, unknown> {
    const placement = LAGUNA_FOOTER_LAYOUT_PLACEMENTS[layout][role];
    const centered = layout === "centered" || layout === "showcase";

    if (role === "footerDivider") {
        return {
            gridH: placement.h,
            gridW: placement.w,
            gridX: placement.x,
            gridY: placement.y,
        };
    }

    return {
        alignment: centered ? "center" : "left",
        contentLayout: role === "footerCopyright"
            ? "inline"
            : role === "footerLinks"
                ? "horizontal"
                : "vertical",
        gap: role === "footerLinks"
            ? layout === "compact" || layout === "minimal"
                ? "36px"
                : "64px"
            : role === "footerCopyright"
                ? "18px"
                : "16px",
        gridH: placement.h,
        gridW: placement.w,
        gridX: placement.x,
        gridY: placement.y,
        verticalAlign: role === "footerCopyright"
            || layout === "compact"
            || layout === "minimal"
            ? "center"
            : "top",
    };
}

function patchNestedAlignment(
    block: ShopTemplateBlock,
    layout: LagunaFooterLayoutStyle,
    parentRole: LagunaFooterRole,
): ShopTemplateBlock {
    const centered = layout === "centered" || layout === "showcase";
    const documentRole = block.options?.[SHOP_DOCUMENT_ROLE_OPTION];
    const options: Record<string, unknown> = { ...(block.options ?? {}) };

    if (
        parentRole === "footerLinks"
        && (
            documentRole === "footerHelp"
            || documentRole === "footerCompany"
            || documentRole === "footerAbout"
        )
    ) {
        options.alignment = centered ? "center" : "left";
    }

    if (
        (
            parentRole === "footerService"
            || parentRole === "footerCopyright"
        )
        && block.type === "text"
    ) {
        options.textAlign = centered ? "center" : "left";
    }

    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(
            ([slotKey, children]) => [
                slotKey,
                children.map((child) => (
                    patchNestedAlignment(child, layout, parentRole)
                )),
            ],
        ))
        : undefined;
    const children = block.children?.map((child) => (
        patchNestedAlignment(child, layout, parentRole)
    ));

    return {
        ...block,
        options,
        ...(slots ? { slots } : {}),
        ...(children ? { children } : {}),
    };
}

function patchRoleLayout(
    block: ShopTemplateBlock,
    layout: LagunaFooterLayoutStyle,
    role: LagunaFooterRole,
): ShopTemplateBlock {
    return patchNestedAlignment({
        ...block,
        options: {
            ...(block.options ?? {}),
            ...roleLayoutOptions(layout, role),
            [SHOP_DOCUMENT_ROLE_OPTION]: role,
        },
    }, layout, role);
}

export function normalizeLagunaFooterLayoutStyle(
    value: unknown,
): LagunaFooterLayoutStyle {
    if (value === "simple") {
        return "minimal";
    }

    return typeof value === "string"
        && (LAGUNA_FOOTER_LAYOUT_STYLES as readonly string[]).includes(value)
        ? value as LagunaFooterLayoutStyle
        : "editorial";
}

export function applyLagunaFooterLayoutSkeleton(
    footer: ShopTemplateBlock,
    layout: LagunaFooterLayoutStyle,
): ShopTemplateBlock {
    if (
        footer.type !== "richContent"
        || footer.options?.lagunaRegion !== "footer"
    ) {
        return footer;
    }

    const currentItems = footer.slots?.[SHOP_GRID_SLOT_KEY] ?? [];
    const patchedItems = currentItems.map((item) => {
        const role = item.options?.[SHOP_DOCUMENT_ROLE_OPTION];

        return typeof role === "string"
            && (LAGUNA_FOOTER_ROLES as readonly string[]).includes(role)
            ? patchRoleLayout(item, layout, role as LagunaFooterRole)
            : item;
    });

    return {
        ...footer,
        options: {
            ...(footer.options ?? {}),
            ...rootLayoutOptions[layout],
            footerLayoutSkeletonVersion: LAGUNA_FOOTER_SKELETON_VERSION,
            footerLayoutStyle: layout,
        },
        slots: {
            ...(footer.slots ?? {}),
            [SHOP_GRID_SLOT_KEY]: patchedItems,
        },
    };
}

export function ensureLagunaFooterLayoutSkeleton(
    footer: ShopTemplateBlock,
    layout: LagunaFooterLayoutStyle,
): ShopTemplateBlock {
    return footer.options?.footerLayoutSkeletonVersion
        === LAGUNA_FOOTER_SKELETON_VERSION
        ? footer
        : applyLagunaFooterLayoutSkeleton(footer, layout);
}
